"use client"

import * as React from "react"
import { Clock } from "lucide-react"
import { TabsContent } from "@/components/ui/tabs"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { UserProfile } from "@/types/workspace"

export interface BarSettingsTabProps {
  topBarConfig: any
  setTopBarConfig: any
  user?: UserProfile | null
}

export function BarSettingsTab({
  topBarConfig,
  setTopBarConfig,
  user,
}: BarSettingsTabProps) {
  return (
    <TabsContent value="bar-tab" className="outline-none space-y-6 animate-in fade-in duration-300 font-sans">
      <div>
        <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
          <Clock className="size-5 text-[#004EB1]" />
          Barra Superior (Bar)
        </h3>
        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
          Personalize os elementos visuais, relógio, clima e a marca exibidos na barra superior.
        </p>
      </div>

      {/* Card 1: Elementos Visíveis na TopBar */}
      <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <CardHeader>
          <CardTitle className="text-sm font-bold text-zinc-800 dark:text-zinc-100 flex items-center gap-2">
            <Clock className="size-4 text-[#004EB1]" />
            Widgets & Exibição na Barra Superior
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Exibição do Relógio */}
            <div className="flex items-center justify-between p-3.5 rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800">
              <span className="text-xs font-bold text-zinc-800 dark:text-zinc-200">Exibir Relógio</span>
              <button
                type="button"
                onClick={() => {
                  const updated = { ...topBarConfig, showClock: !topBarConfig.showClock }
                  setTopBarConfig(updated)
                  if (user?.id) localStorage.setItem(`topBarConfig:${user.id}`, JSON.stringify(updated))
                  localStorage.setItem("bold_topbar_config", JSON.stringify(updated))
                  window.dispatchEvent(new CustomEvent("bold_topbar_config_changed", { detail: updated }))
                }}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${topBarConfig.showClock ? "bg-[#004EB1]" : "bg-zinc-300 dark:bg-zinc-700"
                  }`}
              >
                <span className={`pointer-events-none inline-block size-4 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out ${topBarConfig.showClock ? "translate-x-4" : "translate-x-0"
                  }`} />
              </button>
            </div>

            {/* Exibição da Data */}
            <div className="flex items-center justify-between p-3.5 rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800">
              <span className="text-xs font-bold text-zinc-800 dark:text-zinc-200">Exibir Data</span>
              <button
                type="button"
                onClick={() => {
                  const updated = { ...topBarConfig, showDate: !topBarConfig.showDate }
                  setTopBarConfig(updated)
                  if (user?.id) localStorage.setItem(`topBarConfig:${user.id}`, JSON.stringify(updated))
                  localStorage.setItem("bold_topbar_config", JSON.stringify(updated))
                  window.dispatchEvent(new CustomEvent("bold_topbar_config_changed", { detail: updated }))
                }}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${topBarConfig.showDate ? "bg-[#004EB1]" : "bg-zinc-300 dark:bg-zinc-700"
                  }`}
              >
                <span className={`pointer-events-none inline-block size-4 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out ${topBarConfig.showDate ? "translate-x-4" : "translate-x-0"
                  }`} />
              </button>
            </div>

            {/* Exibição do Clima */}
            <div className="flex items-center justify-between p-3.5 rounded-xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800">
              <span className="text-xs font-bold text-zinc-800 dark:text-zinc-200">Exibir Clima</span>
              <button
                type="button"
                onClick={() => {
                  const updated = { ...topBarConfig, showWeather: !topBarConfig.showWeather }
                  setTopBarConfig(updated)
                  if (user?.id) localStorage.setItem(`topBarConfig:${user.id}`, JSON.stringify(updated))
                  localStorage.setItem("bold_topbar_config", JSON.stringify(updated))
                  window.dispatchEvent(new CustomEvent("bold_topbar_config_changed", { detail: updated }))
                }}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${topBarConfig.showWeather ? "bg-[#004EB1]" : "bg-zinc-300 dark:bg-zinc-700"
                  }`}
              >
                <span className={`pointer-events-none inline-block size-4 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out ${topBarConfig.showWeather ? "translate-x-4" : "translate-x-0"
                  }`} />
              </button>
            </div>
          </div>

          {/* Formatos de Data & Hora */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-zinc-100 dark:border-zinc-800">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300">Formato de Hora</label>
              <select
                value={topBarConfig.timeFormat || "24h"}
                onChange={(e) => {
                  const updated = { ...topBarConfig, timeFormat: e.target.value as "12h" | "24h" }
                  setTopBarConfig(updated)
                  if (user?.id) localStorage.setItem(`topBarConfig:${user.id}`, JSON.stringify(updated))
                  localStorage.setItem("bold_topbar_config", JSON.stringify(updated))
                  window.dispatchEvent(new CustomEvent("bold_topbar_config_changed", { detail: updated }))
                }}
                className="w-full rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-xs px-3 py-2 font-semibold text-zinc-800 dark:text-zinc-200 focus:outline-none"
              >
                <option value="24h">24 Horas (Ex: 14:30)</option>
                <option value="12h">12 Horas AM/PM (Ex: 02:30 PM)</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300">Formato de Data</label>
              <select
                value={topBarConfig.dateFormat || "long"}
                onChange={(e) => {
                  const updated = { ...topBarConfig, dateFormat: e.target.value as "long" | "short" }
                  setTopBarConfig(updated)
                  if (user?.id) localStorage.setItem(`topBarConfig:${user.id}`, JSON.stringify(updated))
                  localStorage.setItem("bold_topbar_config", JSON.stringify(updated))
                  window.dispatchEvent(new CustomEvent("bold_topbar_config_changed", { detail: updated }))
                }}
                className="w-full rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-xs px-3 py-2 font-semibold text-zinc-800 dark:text-zinc-200 focus:outline-none"
              >
                <option value="long">Longo (segunda-feira, 26 de julho)</option>
                <option value="short">Curto (26/07/2026)</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  )
}
